import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ProjectLibrary } from '../../types';
import {
  getProjectLibraries, updateProjectLibraryOrder, deleteProjectLibrary,
} from '../../services/projectLibraryService';
import LibraryPickerModal from '../LibraryPickerModal';
import BoardModal, { BMCancelButton, BMDangerButton } from '../BoardModal';

/**
 * Project Settings — Document Libraries section. Lists linked SharePoint
 * libraries (drag to reorder), add via the picker, remove with confirm.
 */
export default function ProjectLibrariesSection({ pca, projectId }: {
  pca: IPublicClientApplication; projectId: string;
}) {
  const [libraries, setLibraries] = useState<ProjectLibrary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<ProjectLibrary | null>(null);
  const [removing, setRemoving] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return getProjectLibraries(pca, projectId).then(setLibraries).finally(() => setLoading(false));
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const next = [...libraries];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    // Renumber 0..n-1 and persist only the rows whose order actually changed.
    const renumbered = next.map((l, i) => ({ ...l, dagrun_pm_displayorder: i }));
    setLibraries(renumbered);
    const changed = renumbered.filter(l => {
      const prev = libraries.find(x => x.dagrun_pm_projectlibraryid === l.dagrun_pm_projectlibraryid);
      return (prev?.dagrun_pm_displayorder ?? null) !== l.dagrun_pm_displayorder;
    });
    try {
      await Promise.all(changed.map(l => updateProjectLibraryOrder(pca, l.dagrun_pm_projectlibraryid, l.dagrun_pm_displayorder!)));
    } catch {
      reload(); // reconcile on failure
    }
  };

  const doRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await deleteProjectLibrary(pca, confirmRemove.dagrun_pm_projectlibraryid);
      setConfirmRemove(null);
      reload();
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="eyebrow">DOCUMENT LIBRARIES</span>
        <button type="button" className="btn-secondary" onClick={() => setShowPicker(true)}>+ Add Library</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5 }}>
        Link SharePoint document libraries to this project. They appear in the Documents tab and can be
        reordered by dragging.
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading…</div>
      ) : libraries.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)', padding: '12px 0' }}>
          No document libraries connected. Add one to enable document storage for this project.
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="libraries">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {libraries.map((lib, index) => (
                  <Draggable key={lib.dagrun_pm_projectlibraryid} draggableId={lib.dagrun_pm_projectlibraryid} index={index}>
                    {(dp, snapshot) => (
                      <div ref={dp.innerRef} {...dp.draggableProps}
                        style={{
                          ...dp.draggableProps.style,
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px', marginBottom: 6,
                          border: '1px solid var(--border-light)', borderRadius: 6,
                          background: snapshot.isDragging ? 'var(--surface-2)' : '#fff',
                          boxShadow: snapshot.isDragging ? 'var(--shadow-panel)' : 'none',
                        }}>
                        <span {...dp.dragHandleProps} title="Drag to reorder"
                          style={{ cursor: 'grab', color: 'var(--ink-4)', fontSize: 16, lineHeight: 1, userSelect: 'none' }}>⋮⋮</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{lib.dagrun_pm_name}</div>
                          {lib.dagrun_pm_siteurl && (
                            <div style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {lib.dagrun_pm_siteurl}
                            </div>
                          )}
                        </div>
                        <button type="button" onClick={() => setConfirmRemove(lib)}
                          style={{ background: 'transparent', border: 'none', color: '#DC2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          Remove
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {showPicker && (
        <LibraryPickerModal pca={pca} projectId={projectId} existing={libraries}
          onClose={() => setShowPicker(false)}
          onAdded={() => { setShowPicker(false); reload(); }} />
      )}

      {confirmRemove && (
        <BoardModal open title="Remove library" onClose={() => !removing && setConfirmRemove(null)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmRemove(null)} disabled={removing} />
            <BMDangerButton onClick={doRemove} disabled={removing}>{removing ? 'Removing…' : 'Remove'}</BMDangerButton>
          </>}>
          Remove <strong style={{ color: '#0F1117' }}>{confirmRemove.dagrun_pm_name}</strong> from this project?
          The SharePoint library itself is not deleted — only the link.
        </BoardModal>
      )}
    </div>
  );
}
